import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Zap } from 'lucide-react';
import type { LoopHealthDto, RunStatus } from '@aiai/shared';
import { fetchLoopHealth } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { formatCost, formatRelative } from '../lib/format';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { StatusBadge } from './StatusBadge';

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface-2 p-4">
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-ink">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-muted">{sub}</p>}
    </div>
  );
}

function RecentRunRow({
  run,
}: {
  run: LoopHealthDto['recentRuns'][0];
}) {
  const { wp } = useWorkspacePath();
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <Link
          to={wp(`/runs/${run.runId}`)}
          className="block text-sm font-medium text-ink hover:text-signal"
        >
          {run.issueCode && (
            <span className="mr-1.5 font-mono text-xs uppercase tracking-wide text-ink-faint">
              {run.issueCode}
            </span>
          )}
          {run.issueTitle}
        </Link>
        <p className="text-xs text-ink-muted">{formatRelative(run.createdAt)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {run.costUsd != null && (
          <span className="text-xs text-ink-muted">{formatCost(run.costUsd)}</span>
        )}
        <StatusBadge status={run.status as RunStatus} size="sm" />
        {run.pullRequestUrl && (
          <a
            href={run.pullRequestUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-signal hover:underline"
          >
            PR
          </a>
        )}
      </div>
    </div>
  );
}

export function LoopHealthPanel({
  loopId,
}: {
  loopId: string;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: qk.loopHealth(loopId),
    queryFn: () => fetchLoopHealth(loopId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="card p-5">
        <p className="text-sm text-ink-muted">Loading health data…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card p-5">
        <p className="text-sm text-danger-300">
          {errorMessage(error, 'Failed to load loop health')}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const gateTotal = data.gatePassCount + data.gateFailCount;
  const gatePassRate =
    gateTotal > 0 ? Math.round((data.gatePassCount / gateTotal) * 100) : null;
  const successPct =
    data.successRate != null ? Math.round(data.successRate * 100) : null;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-ink">Loop health</h2>
        <p className="mt-0.5 text-xs text-ink-muted">
          Aggregate stats across all firings of this loop.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Firings"
          value={data.firingCount}
          sub={data.firingCount === 1 ? '1 run created' : `${data.firingCount} runs created`}
        />
        <StatCard
          label="Skips"
          value={data.skipCount}
          sub="prior run still active"
        />
        <StatCard
          label="Success rate"
          value={successPct != null ? `${successPct}%` : '—'}
          sub={successPct != null ? 'of completed runs' : 'no completed runs'}
        />
        <StatCard
          label="Total cost"
          value={formatCost(data.totalCostUsd)}
          sub="across all runs"
        />
      </div>

      {gateTotal > 0 && (
        <div className="rounded-lg border border-line bg-surface-2 p-4">
          <p className="mb-2 text-xs font-medium text-ink-muted">Gate results</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-success-500" aria-hidden="true" />
              <span className="text-sm tabular-nums text-ink">
                {data.gatePassCount} passed
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <XCircle className="h-4 w-4 text-danger-500" aria-hidden="true" />
              <span className="text-sm tabular-nums text-ink">
                {data.gateFailCount} failed
              </span>
            </div>
            {gatePassRate != null && (
              <span className="ml-auto text-sm font-medium text-ink">
                {gatePassRate}% pass rate
              </span>
            )}
          </div>
        </div>
      )}

      {data.recentRuns.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium text-ink-muted">Recent runs</p>
          <div className="divide-y divide-line rounded-lg border border-line bg-surface-2 px-4">
            {data.recentRuns.map((run) => (
              <RecentRunRow key={run.runId} run={run} />
            ))}
          </div>
        </div>
      )}

      {data.firingCount === 0 && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Zap className="h-4 w-4" aria-hidden="true" />
          This loop hasn't fired yet. Fire it manually or wait for the next scheduled run.
        </div>
      )}
    </div>
  );
}
